import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { useCart } from "@/context/CartContext";
import {
  customerOrders,
  customerReorder,
  customerAddresses,
  customerAddAddress,
  customerDeleteAddress,
  customerWishlist,
  customerWishlistRemove,
  customerProfileUpdate,
  formatApiError,
} from "@/lib/api";

const TABS = [
  { id: "orders", label: "Orders" },
  { id: "wishlist", label: "Wishlist" },
  { id: "addresses", label: "Addresses" },
  { id: "profile", label: "Profile" },
];

export default function Dashboard() {
  const { customer, logout, refresh } = useCustomerAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("orders");

  const onLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-[var(--st-cream)]">
      <header className="border-b-2 border-[#111] bg-white">
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between">
          <Link
            to="/"
            className="font-heading uppercase tracking-[0.18em] text-xs hover:text-[var(--st-maroon)]"
            data-testid="account-back-home"
          >
            ← Substitute Teacher
          </Link>
          <button
            onClick={onLogout}
            data-testid="account-logout"
            className="font-heading uppercase tracking-widest text-xs px-3 py-1.5 border-2 border-[#111] hover:bg-[var(--st-mustard)]"
          >
            Sign out
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 py-10">
        <div className="mb-8">
          <div className="font-heading uppercase tracking-[0.18em] text-xs text-[var(--st-fg)]/60">
            Welcome back
          </div>
          <h1
            className="font-heading text-4xl md:text-5xl text-[var(--st-fg)]"
            data-testid="account-greeting"
          >
            {customer?.first_name
              ? `Hi, ${customer.first_name}`
              : customer?.email}
          </h1>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-8 border-b-2 border-[#111]">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`account-tab-${t.id}`}
              className={`font-heading uppercase tracking-widest text-xs px-4 py-3 border-2 border-b-0 -mb-[2px] transition-colors ${
                tab === t.id
                  ? "bg-[var(--st-maroon)] text-white border-[var(--st-maroon)]"
                  : "border-transparent text-[var(--st-fg)]/60 hover:text-[var(--st-fg)]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "orders" && <OrdersTab />}
        {tab === "wishlist" && <WishlistTab onChange={refresh} />}
        {tab === "addresses" && <AddressesTab />}
        {tab === "profile" && <ProfileTab onSaved={refresh} />}
      </main>
    </div>
  );
}

/* ─── Orders ──────────────────────────────────────────────────────────────── */
function OrdersTab() {
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const { addBulk } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    customerOrders()
      .then((d) => setItems(d.items || []))
      .catch((e) =>
        setError(formatApiError(e?.response?.data?.detail) || "Failed to load")
      );
  }, []);

  const onReorder = async (orderId) => {
    try {
      const { items: reorderItems } = await customerReorder(orderId);
      if (!reorderItems?.length) {
        toast.error("No items in this order are still available.");
        return;
      }
      addBulk(
        reorderItems.map((it) => ({
          id: it.product_id,
          name: it.name,
          price: it.price,
          image: it.image,
          size: it.size,
          quantity: it.quantity,
        }))
      );
      toast.success(`Re-added ${reorderItems.length} items to your cart`);
      navigate("/checkout");
    } catch (e) {
      toast.error(
        formatApiError(e?.response?.data?.detail) || "Reorder failed"
      );
    }
  };

  if (error)
    return (
      <div className="text-red-700" data-testid="account-orders-error">
        {error}
      </div>
    );
  if (items === null)
    return (
      <div className="text-[var(--st-fg)]/60" data-testid="account-orders-loading">
        Loading orders…
      </div>
    );
  if (!items.length)
    return (
      <div
        className="bg-white border-2 border-[#111] p-8 text-center"
        data-testid="account-orders-empty"
      >
        <div className="font-heading uppercase text-xl mb-2">
          No orders yet
        </div>
        <p className="text-sm text-[var(--st-fg)]/70 mb-4">
          Once you place an order, it&apos;ll show up here.
        </p>
        <Link
          to="/#shop"
          className="inline-block font-display uppercase text-sm px-4 py-2 border-2 border-[#111] bg-[var(--st-gold)] hover:bg-[var(--st-mustard)] shadow-brutal-sm"
          data-testid="account-orders-shop-cta"
        >
          Shop the merch
        </Link>
      </div>
    );

  return (
    <div className="space-y-4" data-testid="account-orders-list">
      {items.map((o) => (
        <div
          key={o.id}
          className="bg-white border-2 border-[#111] shadow-brutal-sm p-5"
          data-testid={`account-order-${o.order_number}`}
        >
          <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
            <div>
              <div className="font-heading uppercase tracking-widest text-xs text-[var(--st-fg)]/60">
                Order {o.order_number}
              </div>
              <div className="text-sm text-[var(--st-fg)]/70">
                {o.created_at
                  ? new Date(o.created_at).toLocaleDateString(undefined, {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })
                  : ""}
              </div>
            </div>
            <span
              className={`font-heading uppercase tracking-widest text-xs px-2 py-1 border-2 border-[#111] ${statusColor(
                o.status
              )}`}
            >
              {o.status || "pending"}
            </span>
          </div>
          <div className="flex flex-wrap gap-3 mb-4">
            {(o.items || []).slice(0, 4).map((it, i) => (
              <div key={i} className="flex items-center gap-2 text-sm">
                <span className="font-body">
                  {it.name}
                  {it.size ? ` · ${it.size}` : ""}
                </span>
                <span className="text-[var(--st-fg)]/50">×{it.quantity}</span>
              </div>
            ))}
            {o.items?.length > 4 && (
              <div className="text-xs text-[var(--st-fg)]/50">
                + {o.items.length - 4} more
              </div>
            )}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-[#11111122]">
            <div className="font-heading text-lg">
              ${Number(o.total || 0).toFixed(2)}{" "}
              <span className="text-xs text-[var(--st-fg)]/50 uppercase">
                {o.currency || "USD"}
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {o.easyship?.tracking_url && (
                <a
                  href={o.easyship.tracking_url}
                  target="_blank"
                  rel="noreferrer"
                  className="font-heading uppercase text-xs px-3 py-1.5 border-2 border-[#111] hover:bg-[var(--st-mustard)]"
                  data-testid={`account-order-track-${o.order_number}`}
                >
                  Track
                </a>
              )}
              <button
                onClick={() => onReorder(o.id)}
                data-testid={`account-order-reorder-${o.order_number}`}
                className="font-heading uppercase text-xs px-3 py-1.5 border-2 border-[#111] bg-[var(--st-gold)] hover:bg-[var(--st-mustard)] shadow-brutal-sm"
              >
                Reorder
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function statusColor(status) {
  switch ((status || "").toLowerCase()) {
    case "paid":
      return "bg-[var(--st-mustard)]";
    case "shipped":
      return "bg-[var(--st-maroon)] text-white border-[var(--st-maroon)]";
    case "delivered":
      return "bg-emerald-500 text-white border-emerald-700";
    case "cancelled":
    case "canceled":
      return "bg-red-200 border-red-700";
    default:
      return "bg-[var(--st-cream)]";
  }
}

/* ─── Wishlist ────────────────────────────────────────────────────────────── */
function WishlistTab() {
  const [items, setItems] = useState(null);
  const { add } = useCart();

  const load = () =>
    customerWishlist().then((d) => setItems(d.items || []));

  useEffect(() => {
    load();
  }, []);

  const onRemove = async (pid) => {
    await customerWishlistRemove(pid);
    setItems((prev) => prev.filter((p) => p.id !== pid));
    toast.success("Removed from wishlist");
  };

  if (items === null) return <div className="text-[var(--st-fg)]/60">Loading…</div>;
  if (!items.length)
    return (
      <div className="bg-white border-2 border-[#111] p-8 text-center" data-testid="account-wishlist-empty">
        <div className="font-heading uppercase text-xl mb-2">No saved items yet</div>
        <p className="text-sm text-[var(--st-fg)]/70 mb-4">
          Hit the heart on any product to save it for later.
        </p>
        <Link
          to="/#shop"
          className="inline-block font-display uppercase text-sm px-4 py-2 border-2 border-[#111] bg-[var(--st-gold)] hover:bg-[var(--st-mustard)] shadow-brutal-sm"
        >
          Browse merch
        </Link>
      </div>
    );

  return (
    <div
      className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4"
      data-testid="account-wishlist-grid"
    >
      {items.map((p) => (
        <div
          key={p.id}
          className="bg-white border-2 border-[#111] shadow-brutal-sm p-3"
          data-testid={`account-wishlist-${p.id}`}
        >
          <div className="aspect-square bg-[var(--st-cream)] mb-3 overflow-hidden">
            {p.image && (
              <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
            )}
          </div>
          <div className="font-heading uppercase text-sm mb-1 line-clamp-2 min-h-[2.5em]">
            {p.name}
          </div>
          <div className="font-display text-lg mb-3">${Number(p.price).toFixed(2)}</div>
          <div className="flex gap-2">
            <button
              onClick={() => add(p, null)}
              className="flex-1 font-heading uppercase text-xs py-2 border-2 border-[#111] bg-[var(--st-gold)] hover:bg-[var(--st-mustard)] shadow-brutal-sm"
            >
              Add to cart
            </button>
            <button
              onClick={() => onRemove(p.id)}
              className="font-heading uppercase text-xs px-3 py-2 border-2 border-[#111] hover:bg-red-100"
              data-testid={`account-wishlist-remove-${p.id}`}
            >
              ✕
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ─── Addresses ───────────────────────────────────────────────────────────── */
function AddressesTab() {
  const [items, setItems] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = () => customerAddresses().then((d) => setItems(d.items || []));
  useEffect(() => {
    load();
  }, []);

  const onAdded = (addr) => {
    setItems((prev) => [...(prev || []), addr]);
    setShowForm(false);
  };
  const onDelete = async (id) => {
    await customerDeleteAddress(id);
    setItems((prev) => prev.filter((a) => a.id !== id));
  };

  if (items === null) return <div className="text-[var(--st-fg)]/60">Loading…</div>;

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button
          onClick={() => setShowForm((s) => !s)}
          data-testid="account-address-add-toggle"
          className="font-heading uppercase text-xs px-3 py-2 border-2 border-[#111] bg-[var(--st-gold)] hover:bg-[var(--st-mustard)] shadow-brutal-sm"
        >
          {showForm ? "Cancel" : "+ Add address"}
        </button>
      </div>

      {showForm && <AddressForm onAdded={onAdded} />}

      {!items.length && !showForm && (
        <div className="bg-white border-2 border-[#111] p-8 text-center" data-testid="account-addresses-empty">
          <div className="font-heading uppercase text-xl mb-2">No saved addresses</div>
          <p className="text-sm text-[var(--st-fg)]/70">
            Add an address for one-click checkout.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        {items.map((a) => (
          <div
            key={a.id}
            className="bg-white border-2 border-[#111] shadow-brutal-sm p-4"
            data-testid={`account-address-${a.id}`}
          >
            <div className="font-heading uppercase tracking-widest text-xs mb-2">
              {a.label || "Address"}
            </div>
            <div className="text-sm leading-relaxed">
              {a.line1}
              {a.line2 ? `, ${a.line2}` : ""}
              <br />
              {a.city}, {a.state} {a.postal_code}
              <br />
              {a.country}
            </div>
            <button
              onClick={() => onDelete(a.id)}
              data-testid={`account-address-delete-${a.id}`}
              className="mt-3 font-heading uppercase text-xs px-3 py-1.5 border-2 border-[#111] hover:bg-red-100"
            >
              Delete
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function AddressForm({ onAdded }) {
  const [f, setF] = useState({
    label: "Home",
    line1: "",
    line2: "",
    city: "",
    state: "",
    postal_code: "",
    country: "US",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const onChange = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const addr = await customerAddAddress(f);
      onAdded(addr);
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <form
      onSubmit={onSubmit}
      className="bg-white border-2 border-[#111] shadow-brutal-sm p-4 mb-4 grid grid-cols-2 gap-3"
      data-testid="account-address-form"
    >
      <Field label="Label" v={f.label} on={onChange("label")} />
      <Field label="Country" v={f.country} on={onChange("country")} maxLength={2} />
      <Field label="Address line 1" v={f.line1} on={onChange("line1")} required full />
      <Field label="Address line 2" v={f.line2} on={onChange("line2")} full />
      <Field label="City" v={f.city} on={onChange("city")} required />
      <Field label="State" v={f.state} on={onChange("state")} required />
      <Field label="Postal code" v={f.postal_code} on={onChange("postal_code")} required />
      <div />
      {error && (
        <div className="col-span-2 text-sm text-red-700 bg-red-100 border-2 border-red-700 px-3 py-2">
          {error}
        </div>
      )}
      <div className="col-span-2 flex justify-end">
        <button
          type="submit"
          disabled={busy}
          data-testid="account-address-save"
          className="font-heading uppercase text-xs px-4 py-2 border-2 border-[#111] bg-[var(--st-gold)] hover:bg-[var(--st-mustard)] shadow-brutal-sm disabled:opacity-60"
        >
          {busy ? "Saving…" : "Save address"}
        </button>
      </div>
    </form>
  );
}

function Field({ label, v, on, required, full, maxLength }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <label className="block text-xs font-heading uppercase tracking-widest text-[var(--st-fg)]/70 mb-1">
        {label}
        {required && " *"}
      </label>
      <input
        value={v}
        onChange={on}
        required={!!required}
        maxLength={maxLength}
        className="w-full border-2 border-[#111] bg-[var(--st-cream)] px-3 py-2 font-body text-base focus:outline-none focus:bg-white"
      />
    </div>
  );
}

/* ─── Profile ─────────────────────────────────────────────────────────────── */
function ProfileTab({ onSaved }) {
  const { customer } = useCustomerAuth();
  const [f, setF] = useState({
    first_name: customer?.first_name || "",
    last_name: customer?.last_name || "",
    phone: customer?.phone || "",
  });
  const [busy, setBusy] = useState(false);
  const onChange = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await customerProfileUpdate(f);
      toast.success("Profile updated");
      await onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <form
      onSubmit={onSubmit}
      className="bg-white border-2 border-[#111] shadow-brutal-sm p-6 grid grid-cols-2 gap-4 max-w-2xl"
      data-testid="account-profile-form"
    >
      <Field label="First name" v={f.first_name} on={onChange("first_name")} />
      <Field label="Last name" v={f.last_name} on={onChange("last_name")} />
      <div className="col-span-2">
        <label className="block text-xs font-heading uppercase tracking-widest text-[var(--st-fg)]/70 mb-1">
          Email
        </label>
        <input
          value={customer?.email || ""}
          disabled
          className="w-full border-2 border-[#111] bg-[var(--st-cream)]/60 px-3 py-2 font-body text-base text-[var(--st-fg)]/60"
        />
      </div>
      <div className="col-span-2">
        <label className="block text-xs font-heading uppercase tracking-widest text-[var(--st-fg)]/70 mb-1">
          Phone
        </label>
        <input
          value={f.phone}
          onChange={onChange("phone")}
          className="w-full border-2 border-[#111] bg-[var(--st-cream)] px-3 py-2 font-body text-base focus:outline-none focus:bg-white"
          data-testid="account-profile-phone"
        />
      </div>
      <div className="col-span-2 flex justify-end">
        <button
          type="submit"
          disabled={busy}
          data-testid="account-profile-save"
          className="font-heading uppercase text-xs px-4 py-2 border-2 border-[#111] bg-[var(--st-gold)] hover:bg-[var(--st-mustard)] shadow-brutal-sm disabled:opacity-60"
        >
          {busy ? "Saving…" : "Save profile"}
        </button>
      </div>
    </form>
  );
}
